/**
 * Transfer / exchange / loan — the three ways money moves between accounts.
 *
 * One dialog with three modes rather than three dialogs: they share the same
 * from/to/amount skeleton, and having them side by side in a tab strip is also
 * how the user learns which one they want. The rules that distinguish them
 * (same currency vs different, plain vs owed back) are enforced by the server;
 * this mirrors them so the mistake is caught before the request.
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/misc';
import { ResponsiveDialog } from '@/components/ui/dialog';
import { AmountInput } from '@/components/AmountInput';
import { AccountPicker } from '@/components/AccountPicker';
import { checkMovement, isCompleteRef, type MovementMode } from '@/lib/movement';
import { formatRate, todayIso, toCanonicalAmount } from '@/lib/format';
import {
  useCreateExchange,
  useCreateLoan,
  useCreateTransfer,
  useCurrencies,
  useFxRates,
} from '@/hooks/useApi';
import type { AccountRef } from '@/lib/types';

export type { MovementMode };

const TITLES: Record<MovementMode, string> = {
  transfer: 'Прехвърляне между каси или места',
  exchange: 'Обмяна на валута',
  loan: 'Заем между каси',
};

const DESCRIPTIONS: Record<MovementMode, string> = {
  transfer: 'Едни и същи пари сменят мястото или касата си. Общата сума не се променя.',
  exchange: 'Едната валута става друга. Въведи колко излиза и колко реално получаваш.',
  loan: 'Прехвърляне, което се очаква обратно. Остава на началния екран, докато не се погаси.',
};

export function MovementDialog({
  mode,
  onClose,
  defaultFrom,
  defaultTo,
}: {
  mode: MovementMode | null;
  onClose: () => void;
  defaultFrom?: Partial<AccountRef>;
  defaultTo?: Partial<AccountRef>;
}): JSX.Element {
  const [tab, setTab] = useState<MovementMode>('transfer');
  const [from, setFrom] = useState<Partial<AccountRef>>({});
  const [to, setTo] = useState<Partial<AccountRef>>({});
  const [amountRaw, setAmountRaw] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [toAmountRaw, setToAmountRaw] = useState('');
  const [toAmountMinor, setToAmountMinor] = useState<number | null>(null);
  const [date, setDate] = useState(todayIso());
  const [dueAt, setDueAt] = useState('');
  const [note, setNote] = useState('');

  const { data: currencies = [] } = useCurrencies();
  const { data: fx } = useFxRates();
  const createTransfer = useCreateTransfer();
  const createExchange = useCreateExchange();
  const createLoan = useCreateLoan();

  const open = mode != null;

  useEffect(() => {
    if (!mode) return;
    setTab(mode);
    setFrom(defaultFrom ?? {});
    setTo(defaultTo ?? {});
    setAmountRaw('');
    setAmountMinor(null);
    setToAmountRaw('');
    setToAmountMinor(null);
    setDate(todayIso());
    setDueAt('');
    setNote('');
  }, [mode, defaultFrom, defaultTo]);

  const fromDecimals = currencies.find((c) => c.code === from.currency)?.decimals ?? 2;

  /**
   * Market rate for the pair, so the user can see what their exchange cost
   * them against the reference. Stored rates are quoted per EUR, so crossing
   * two of them gives the pair.
   */
  const marketRate = useMemo(() => {
    if (tab !== 'exchange' || !from.currency || !to.currency || !fx) return null;
    const rateOf = (code: string) =>
      code === fx.base ? 1 : Number(fx.rates.find((r) => r.quote === code)?.rate ?? NaN);
    const f = rateOf(from.currency);
    const t = rateOf(to.currency);
    if (!Number.isFinite(f) || !Number.isFinite(t) || f === 0) return null;
    return t / f;
  }, [tab, from.currency, to.currency, fx]);

  const { toRef, problem, missing, canSave } = checkMovement({
    mode: tab,
    from,
    to,
    amountMinor,
    toAmountMinor,
  });

  // Follows the RESOLVED destination currency: for a transfer or loan the raw
  // form state has none, because that field is locked.
  const toDecimals = currencies.find((c) => c.code === toRef.currency)?.decimals ?? 2;

  const impliedRate =
    amountMinor && toAmountMinor
      ? toAmountMinor / 10 ** toDecimals / (amountMinor / 10 ** fromDecimals)
      : null;

  const spreadPercent =
    impliedRate && marketRate ? (impliedRate - marketRate) / marketRate : null;


  const pending = createTransfer.isPending || createExchange.isPending || createLoan.isPending;

  const handleSave = async () => {
    if (!canSave || !isCompleteRef(from) || !isCompleteRef(toRef) || amountMinor == null) return;
    const amount = toCanonicalAmount(amountMinor, fromDecimals);

    try {
      if (tab === 'transfer') {
        await createTransfer.mutateAsync({ date, amount, from, to: toRef, note: note || undefined });
        toast.success('Прехвърлянето е записано');
      } else if (tab === 'exchange') {
        await createExchange.mutateAsync({
          date,
          from,
          to: toRef,
          fromAmount: amount,
          toAmount: toCanonicalAmount(toAmountMinor!, toDecimals),
          note: note || undefined,
        });
        toast.success('Обмяната е записана');
      } else {
        await createLoan.mutateAsync({
          date,
          amount,
          from,
          to: toRef,
          dueAt: dueAt || null,
          note: note || undefined,
        });
        toast.success('Заемът е записан и ще стои на началния екран, докато не се погаси');
      }
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={TITLES[tab]}
      description={DESCRIPTIONS[tab]}
      className="sm:max-w-lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-1 rounded-md bg-muted p-1">
          {(
            [
              ['transfer', 'Прехвърляне'],
              ['exchange', 'Обмяна'],
              ['loan', 'Заем'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={cn(
                'rounded-sm px-2 py-2 text-sm font-medium transition-colors',
                tab === value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <AccountPicker
          value={from}
          onChange={setFrom}
          label={tab === 'loan' ? 'От коя каса (кредитор)' : 'От'}
          idPrefix="mv-from"
        />

        <div className="flex justify-center">
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>

        <AccountPicker
          value={toRef}
          onChange={setTo}
          label={tab === 'loan' ? 'Към коя каса (длъжник)' : 'Към'}
          // A transfer is same-currency by definition; locking it removes an
          // error the user would otherwise have to discover. `toRef` carries
          // the locked value so the picker's balance hint resolves too.
          lockCurrency={tab !== 'exchange' ? from.currency : undefined}
          idPrefix="mv-to"
        />

        <div className={cn('grid gap-3', tab === 'exchange' && 'sm:grid-cols-2')}>
          <div className="space-y-1.5">
            <Label htmlFor="mv-amount">{tab === 'exchange' ? 'Излиза' : 'Сума'}</Label>
            <AmountInput
              id="mv-amount"
              value={amountRaw}
              onChange={setAmountRaw}
              onValueChange={setAmountMinor}
              currency={from.currency ?? 'EUR'}
              decimals={fromDecimals}
            />
          </div>

          {tab === 'exchange' ? (
            <div className="space-y-1.5">
              <Label htmlFor="mv-to-amount">Получавам</Label>
              <AmountInput
                id="mv-to-amount"
                value={toAmountRaw}
                onChange={setToAmountRaw}
                onValueChange={setToAmountMinor}
                currency={to.currency ?? 'EUR'}
                decimals={toDecimals}
              />
            </div>
          ) : null}
        </div>

        {tab === 'exchange' && impliedRate ? (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Твоят курс </span>
            <span className="tabular font-medium">{formatRate(impliedRate)}</span>
            {marketRate ? (
              <>
                <span className="text-muted-foreground"> · пазарен </span>
                <span className="tabular">{formatRate(marketRate)}</span>
                {spreadPercent != null ? (
                  <span
                    className={cn(
                      'tabular ml-2',
                      spreadPercent < -0.005 ? 'text-expense' : 'text-muted-foreground',
                    )}
                  >
                    ({spreadPercent > 0 ? '+' : '−'}
                    {Math.abs(spreadPercent * 100).toFixed(2)}%)
                  </span>
                ) : null}
              </>
            ) : null}
            <p className="mt-1 text-xs text-muted-foreground">
              Разликата спрямо пазарния курс е информативна — не се осчетоводява.
            </p>
          </div>
        ) : null}

        <div className={cn('grid gap-3', tab === 'loan' && 'sm:grid-cols-2')}>
          <div className="space-y-1.5">
            <Label htmlFor="mv-date">Дата</Label>
            <Input
              id="mv-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={todayIso()}
            />
          </div>
          {tab === 'loan' ? (
            <div className="space-y-1.5">
              <Label htmlFor="mv-due">Краен срок (по избор)</Label>
              <Input
                id="mv-due"
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                min={date}
              />
            </div>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="mv-note">Бележка</Label>
          <Textarea id="mv-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        </div>

        {problem ? (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {problem}
          </p>
        ) : missing ? (
          <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {missing}
          </p>
        ) : null}

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Откажи
          </Button>
          <Button className="flex-1" disabled={!canSave || pending} onClick={() => void handleSave()}>
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Запиши
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
